import type { USAStateAcronym } from '@hc-frontend/core-types';
import type { IOperationResult } from '@hc-frontend/core-utils-business-operation';

/**
 * The current consent status of the user
 */
export enum ConsentStatus {
  /**
   * User has not yet interacted with the consent banner
   */
  UNKNOWN = 'unknown',
  /**
   * User has accepted all cookies including non-essential tracking
   */
  ALL_COOKIES = 'all_cookies',
  /**
   * User has accepted only required cookies, rejecting non-essential tracking
   */
  REQUIRED_COOKIES = 'required_cookies',
  /**
   * User is not in a jurisdiction requiring consent
   */
  NOT_REQUIRED = 'not_required',
}

/**
 * States that require strict consent management (CCPA, privacy regulations)
 * This can be expanded to include other privacy-regulation states
 */
export enum StrictConsentState {
  CALIFORNIA = 'CA',
  // Future additions: Nevada, Colorado, Virginia, etc.
}

/**
 * The user's consent choice
 */
export type ConsentChoice = 'all_cookies' | 'required_cookies';

/**
 * Repository interface for persisting consent data
 */
export interface ConsentStorageRepository {
  /**
   * Get the stored consent status
   */
  getConsentStatus: () => ConsentStatus;

  /**
   * Save the user's consent choice
   */
  saveConsent: (choice: ConsentChoice) => void;

  /**
   * Clear the consent data
   */
  clearConsent: () => void;
}

/**
 * Get the current consent status
 */
export type GetConsentStatus = () => Promise<
  IOperationResult<ConsentStatus, never>
>;

/**
 * Save user's consent choice
 */
export type SaveConsent = (
  choice: ConsentChoice,
) => Promise<IOperationResult<void, never>>;

/**
 * Determine if consent banner should be shown
 */
export type ShouldShowConsent = (params: {
  stateAcronym: USAStateAcronym;
}) => Promise<IOperationResult<boolean, never>>;

/**
 * Check if user is in a state requiring strict consent
 */
export type IsInStrictConsentState = (params: {
  stateAcronym: USAStateAcronym;
}) => boolean;
